<template>
    <canvas ref="canvas" height="256" style="width: 100%;" />
</template>

<script>
const COLOR_EARTH = 'rgba(99, 130, 180, 0.85)'
const COLOR_SUN   = 'rgba(245, 185, 50, 0.85)'

const PLANET_LABELS = {
    Mercury: 'Merc.',
    Neptune: 'Nept.',
}

function selectUnit(maxKm) {
    if (maxKm >= 1e9) return { divisor: 1e9, label: 'B km' }
    if (maxKm >= 1e6) return { divisor: 1e6, label: 'M km' }
    return { divisor: 1e3, label: 'K km' }
}

function niceMax(valInUnit) {
    const mag = Math.pow(10, Math.floor(Math.log10(valInUnit)))
    return Math.ceil(valInUnit / mag) * mag
}

export default {
    name: 'PlanetDistanceChart',
    props: {
        planets:   { type: Array,  default: () => [] },
        title:     { type: String, default: '' },
        subtitle:  { type: String, default: '' },
        subtitle2: { type: String, default: '' },
    },
    watch: {
        planets() { this.redraw() },
    },
    mounted() {
        this.redraw()
    },
    methods: {
        redraw() {
            const canvas = this.$refs.canvas
            if (!canvas || !this.planets.length) return

            const dpr  = window.devicePixelRatio || 1
            const cssW = canvas.parentElement ? canvas.parentElement.clientWidth : 600
            const cssH = parseInt(canvas.style.height) || canvas.height
            canvas.width  = cssW * dpr
            canvas.height = cssH * dpr
            canvas.style.height = cssH + 'px'
            const ctx = canvas.getContext('2d')
            ctx.scale(dpr, dpr)
            const w = cssW
            const h = cssH

            const PAD_TOP    = 68
            const PAD_BOTTOM = 64
            const PAD_LEFT   = 48
            const PAD_RIGHT  = 16
            const plotW = w - PAD_LEFT - PAD_RIGHT
            const plotH = h - PAD_TOP - PAD_BOTTOM
            const plotBottom = PAD_TOP + plotH

            ctx.clearRect(0, 0, w, h)

            if (this.title) {
                ctx.font = 'bold 11px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.6)'
                ctx.fillText(this.title, PAD_LEFT, 16)
            }
            if (this.subtitle) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle, PAD_LEFT, 28)
            }
            if (this.subtitle2) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle2, PAD_LEFT, 40)
            }

            // Unit scaling
            const allKm = this.planets.flatMap(p => [
                p.distance_to_earth.distance_km,
                p.distance_to_sun.distance_km,
            ])
            const maxKm = Math.max(...allKm)
            const { divisor, label: unitLabel } = selectUnit(maxKm)
            const maxInUnit = niceMax(maxKm / divisor)
            const tickStep  = maxInUnit / 4

            const toY = km => plotBottom - (km / divisor / maxInUnit) * plotH

            // Unit label above y-axis
            ctx.font = '10px system-ui, sans-serif'
            ctx.textAlign = 'left'
            ctx.fillStyle = 'rgba(0,0,0,0.35)'
            ctx.fillText(unitLabel, PAD_LEFT, PAD_TOP - 8)

            // Grid lines + y-axis labels
            for (let i = 0; i <= 4; i++) {
                const val = tickStep * i
                const y   = toY(val * divisor)
                ctx.beginPath()
                ctx.moveTo(PAD_LEFT, y)
                ctx.lineTo(PAD_LEFT + plotW, y)
                ctx.strokeStyle = 'rgba(0,0,0,0.07)'
                ctx.lineWidth = 1
                ctx.setLineDash([4, 4])
                ctx.stroke()
                ctx.setLineDash([])
                ctx.textAlign = 'right'
                ctx.fillStyle = 'rgba(0,0,0,0.35)'
                ctx.fillText(val.toFixed(1), PAD_LEFT - 8, y + 4)
            }

            // Bars
            const n      = this.planets.length
            const groupW = plotW / n
            const barW   = groupW * 0.32
            const barGap = groupW * 0.08
            const gPad   = (groupW - barW * 2 - barGap) / 2

            ctx.font = '10px system-ui, sans-serif'
            this.planets.forEach((planet, i) => {
                const groupX = PAD_LEFT + i * groupW

                // Earth bar
                const earthY = toY(planet.distance_to_earth.distance_km)
                ctx.fillStyle = COLOR_EARTH
                ctx.fillRect(groupX + gPad, earthY, barW, plotBottom - earthY)

                // Sun bar
                const sunY = toY(planet.distance_to_sun.distance_km)
                ctx.fillStyle = COLOR_SUN
                ctx.fillRect(groupX + gPad + barW + barGap, sunY, barW, plotBottom - sunY)

                // Planet name
                ctx.textAlign = 'center'
                ctx.fillStyle = 'rgba(0,0,0,0.5)'
                ctx.fillText(PLANET_LABELS[planet.planet_name] || planet.planet_name, groupX + groupW / 2, plotBottom + 14)
            })

            // Legend
            const SWATCH_W = 16, SWATCH_H = 8, ITEM_GAP = 24
            ctx.font = '11px system-ui, sans-serif'
            const label1 = 'To Earth'
            const label2 = 'To Sun'
            const totalW = SWATCH_W + 8 + ctx.measureText(label1).width + ITEM_GAP +
                           SWATCH_W + 8 + ctx.measureText(label2).width
            let lx = (w - totalW) / 2
            const legendY = h - 8

            for (const [color, label] of [[COLOR_EARTH, label1], [COLOR_SUN, label2]]) {
                ctx.fillStyle = color
                ctx.fillRect(lx, legendY - SWATCH_H, SWATCH_W, SWATCH_H)
                ctx.strokeStyle = 'rgba(0,0,0,0.15)'
                ctx.lineWidth = 1
                ctx.strokeRect(lx, legendY - SWATCH_H, SWATCH_W, SWATCH_H)
                lx += SWATCH_W + 8
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.5)'
                ctx.fillText(label, lx, legendY)
                lx += ctx.measureText(label).width + ITEM_GAP
            }
        },
    },
}
</script>
